/**
 * Inbox-specific derivations.
 *
 * The Inbox is a focused queue: only `pending_confirmation` engagements where
 * `awaiting='governance'` and the viewer is the governance approver. All
 * derivations here assume that filter.
 *
 * General-purpose helpers (NTE math, threshold delta, age in queue, red flags)
 * live in `components/shared/listHelpers.ts` and are reused by the
 * Fulfillment Feed.
 */
import type { EngagementScene, PendingConfirmationEngagement } from '../../types';

/**
 * Filter scenes down to engagements awaiting the given viewer's governance
 * approval. Spec §9.7: viewer-scoped queue (only the engagements where the
 * current user is the governance_approver_id and the engagement is awaiting
 * their decision).
 */
export function rowsForViewer(scenes: EngagementScene[], viewerUserId: string): EngagementScene[] {
  return scenes.filter((s) => {
    const e = s.engagement;
    return (
      e.status === 'pending_confirmation' &&
      e.awaiting === 'governance' &&
      e.governance_approver_id === viewerUserId
    );
  });
}

/**
 * Sort scenes oldest-first by `customer_confirmed_at`. The clock for the
 * governance queue starts when the customer accepts (spec §7 strictly serial
 * sub-states), not at engagement creation. Oldest-first surfaces the rows
 * that have been waiting longest — those are the ones most at risk of
 * tripping the §11.2 #6 "governance pending >3 days" red flag.
 */
export function sortByQueueAge(scenes: EngagementScene[]): EngagementScene[] {
  return [...scenes].sort((a, b) => {
    const aTs = (a.engagement as PendingConfirmationEngagement).customer_confirmed_at ?? '';
    const bTs = (b.engagement as PendingConfirmationEngagement).customer_confirmed_at ?? '';
    return aTs.localeCompare(bTs);
  });
}

/**
 * Truncate a string at `maxChars`, appending an ellipsis when truncated.
 * Spec §9.7: scope summary capped at 200 characters per row.
 *
 * Truncation respects word boundaries when possible — backs up to the last
 * whitespace before `maxChars` rather than slicing mid-word, but only if the
 * back-off doesn't lose more than ~20% of the available chars.
 */
export function truncate(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace > maxChars * 0.8) {
    return `${slice.slice(0, lastSpace)}…`;
  }
  return `${slice}…`;
}
